import type { Socket } from 'socket.io-client'
import {
  restStartMvMAttack,
  restMvmAttackArrived,
  restMvmDefenderSelect,
  restMvmDefenderSelectAndConfirm,
  restMvmDefenderTowerMarch,
  restMvmConfirmBattle,
  restMvmAutoDefend,
  restJoinMvMRally,
  restJoinMvMReinforcement,
  restLeaveMvMRallyJoin,
  restLeaveMvMReinforcement,
  restRequestDefenseHelp,
  restMvmWorldAttackPrecheck,
} from './mvmPendingRest'

let mvMPendingSocketRef: Socket | null = null

/** Same Socket.IO instance as GlobalAttackAlert — required for socket-first pending ops. */
export function registerMvMPendingSocket(socket: Socket | null) {
  mvMPendingSocketRef = socket
}

/** Read-only access for UI (e.g. alliance roster panel) to follow the same connection as GlobalAttackAlert. */
export function getMvMPendingSocket(): Socket | null {
  return mvMPendingSocketRef
}

const ACK_TIMEOUT_MS = 20000

/** One in-flight logical request per key (double-click / parallel socket+REST). */
const inflightMvmOps = new Map<string, Promise<unknown>>()

function newMvmClientRequestId(): string {
  try {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') return crypto.randomUUID()
  } catch {
    /* fall through */
  }
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}-${Math.random().toString(36).slice(2, 10)}`
}

function dedupeMvmOp<T>(key: string, run: () => Promise<T>): Promise<T> {
  const existing = inflightMvmOps.get(key)
  if (existing !== undefined) return existing as Promise<T>
  const p = run().finally(() => {
    if (inflightMvmOps.get(key) === p) inflightMvmOps.delete(key)
  })
  inflightMvmOps.set(key, p)
  return p as Promise<T>
}

function emitWithAck<T>(event: string, payload: Record<string, unknown>, restFallback: () => Promise<T>): Promise<T> {
  const sock = mvMPendingSocketRef
  if (!sock?.connected) {
    return restFallback()
  }
  return new Promise((resolve, reject) => {
    let settled = false
    const timer = setTimeout(() => {
      if (settled) return
      settled = true
      restFallback().then(resolve).catch(reject)
    }, ACK_TIMEOUT_MS)
    sock.emit(event, payload, (res: unknown) => {
      if (settled) return
      settled = true
      clearTimeout(timer)
      const r = res as { success?: boolean; message?: string; error?: string; status?: number }
      if (r?.success) {
        const { success: _s, error: _e, message: _m, status: _st, ...data } = r as Record<string, unknown>
        resolve(data as T)
        return
      }
      const msg = r?.message || r?.error || 'Request failed'
      const err = new Error(msg) as Error & { response?: { data: { message: string }; status?: number } }
      err.response = { data: { message: msg }, status: r?.status ?? 400 }
      reject(err)
    })
  })
}

export type MvmWorldAttackPrecheckExtras = {
  leaderCharacterCount?: number
  estimatedMarchTimeMs?: number
  worldId?: string
  castleSiegePrecheck?: boolean
}

/** Socket ACK `mvm:world-attack-precheck` with REST fallback — same rules as POST /mvm/world-attack-precheck. */
export const mvmWorldAttackPrecheck = (
  walletAddress: string,
  targetOwnerWallet: string,
  extras?: MvmWorldAttackPrecheckExtras,
) => {
  const w = (walletAddress || '').toLowerCase().trim()
  const t = (targetOwnerWallet || '').toLowerCase().trim()
  const wid = extras?.worldId || ''
  const siege = extras?.castleSiegePrecheck ? '1' : '0'
  const key = `waprecheck|${w}|${t}|${wid}|${siege}`
  return dedupeMvmOp(key, () =>
    emitWithAck(
      'mvm:world-attack-precheck',
      {
        walletAddress,
        targetOwnerWallet,
        ...(typeof extras?.leaderCharacterCount === 'number' && Number.isFinite(extras.leaderCharacterCount)
          ? { leaderCharacterCount: extras.leaderCharacterCount }
          : {}),
        ...(typeof extras?.estimatedMarchTimeMs === 'number' && Number.isFinite(extras.estimatedMarchTimeMs)
          ? { estimatedMarchTimeMs: extras.estimatedMarchTimeMs }
          : {}),
        ...(extras?.worldId ? { worldId: extras.worldId } : {}),
        ...(extras?.castleSiegePrecheck ? { castleSiegePrecheck: true } : {}),
      },
      () => restMvmWorldAttackPrecheck(walletAddress, targetOwnerWallet, extras),
    )
  )
}

export const startMvMAttack = (
  walletAddress: string,
  targetOwnerWallet: string,
  fromQ: number,
  fromR: number,
  toQ: number,
  toR: number,
  selectedCharacterIds: string[],
  abilityPoints?: unknown,
  characterTemplateIds?: string[],
  rallyMode?: boolean,
  rallyDurationMs?: number,
  worldId?: string,
  breadAmount?: number
) => {
  const w = (walletAddress || '').toLowerCase().trim()
  const t = (targetOwnerWallet || '').toLowerCase().trim()
  const charsKey = [...selectedCharacterIds].map((id) => String(id)).sort().join(',')
  const wid = worldId || ''
  const key = `start|${w}|${t}|${fromQ}|${fromR}|${toQ}|${toR}|${!!rallyMode}|${wid}|${charsKey}|${breadAmount ?? ''}`
  // One id per launch: when the socket ack times out and the REST fallback re-sends the launch, the
  // server recognises it as the SAME launch and answers with the march it already created.
  const clientRequestId = newMvmClientRequestId()
  return dedupeMvmOp(key, () =>
    emitWithAck(
      'mvm:start-attack',
      {
        walletAddress,
        targetOwnerWallet,
        fromQ,
        fromR,
        toQ,
        toR,
        selectedCharacterIds,
        characterTemplateIds,
        rallyMode: !!rallyMode,
        ...(typeof rallyDurationMs === 'number' ? { rallyDurationMs } : {}),
        ...(worldId ? { worldId } : {}),
        ...(typeof breadAmount === 'number' && Number.isFinite(breadAmount) ? { breadAmount } : {}),
        clientRequestId,
      },
      () =>
        restStartMvMAttack(
          walletAddress,
          targetOwnerWallet,
          fromQ,
          fromR,
          toQ,
          toR,
          selectedCharacterIds,
          abilityPoints,
          characterTemplateIds,
          rallyMode,
          rallyDurationMs,
          worldId,
          breadAmount,
          clientRequestId
        )
    )
  )
}

export const mvmAttackArrived = (walletAddress: string, attackId: string) => {
  const w = (walletAddress || '').toLowerCase().trim()
  return dedupeMvmOp(`arrived|${attackId}|${w}`, () =>
    emitWithAck('mvm:attack-arrived', { walletAddress, attackId }, () => restMvmAttackArrived(walletAddress, attackId))
  )
}

export const mvmDefenderSelect = (
  walletAddress: string,
  attackId: string,
  selectedCharacterIds: string[],
  abilityPoints?: unknown,
  characterTemplateIds?: string[],
  breadAmount?: number
) => {
  const w = (walletAddress || '').toLowerCase().trim()
  const ck = [...selectedCharacterIds].map((id) => String(id)).sort().join(',')
  return dedupeMvmOp(`defsel|${attackId}|${w}|${ck}`, () =>
    emitWithAck(
      'mvm:defender-select',
      {
        walletAddress,
        attackId,
        selectedCharacterIds,
        characterTemplateIds,
        ...(typeof breadAmount === 'number' && Number.isFinite(breadAmount) ? { breadAmount } : {}),
      },
      () => restMvmDefenderSelect(walletAddress, attackId, selectedCharacterIds, abilityPoints, characterTemplateIds, breadAmount)
    )
  )
}

export const mvmDefenderSelectAndConfirm = (
  walletAddress: string,
  attackId: string,
  selectedCharacterIds: string[],
  abilityPoints?: unknown,
  characterTemplateIds?: string[],
  breadAmount?: number
) => {
  const w = (walletAddress || '').toLowerCase().trim()
  const ck = [...selectedCharacterIds].map((id) => String(id)).sort().join(',')
  return dedupeMvmOp(`defselconfirm|${attackId}|${w}|${ck}`, () =>
    emitWithAck(
      'mvm:defender-select-and-confirm',
      {
        walletAddress,
        attackId,
        selectedCharacterIds,
        characterTemplateIds,
        ...(typeof breadAmount === 'number' && Number.isFinite(breadAmount) ? { breadAmount } : {}),
      },
      () =>
        restMvmDefenderSelectAndConfirm(
          walletAddress,
          attackId,
          selectedCharacterIds,
          abilityPoints,
          characterTemplateIds,
          breadAmount
        )
    )
  )
}

/** Tower PvP: defender march to the boundary tower (socket ACK with REST fallback). */
export const mvmDefenderTowerMarch = (
  walletAddress: string,
  attackId: string,
  selectedCharacterIds: string[],
  abilityPoints?: unknown,
  characterTemplateIds?: string[],
  worldId?: string
) => {
  const w = (walletAddress || '').toLowerCase().trim()
  const ck = [...selectedCharacterIds].map((id) => String(id)).sort().join(',')
  const wid = worldId || ''
  return dedupeMvmOp(`towermarch|${attackId}|${w}|${ck}|${wid}`, () =>
    emitWithAck(
      'mvm:defender-tower-march',
      {
        walletAddress,
        attackId,
        selectedCharacterIds,
        characterTemplateIds,
        ...(typeof abilityPoints !== 'undefined' ? { abilityPoints } : {}),
        ...(worldId ? { worldId } : {}),
      },
      () =>
        restMvmDefenderTowerMarch(
          walletAddress,
          attackId,
          selectedCharacterIds,
          abilityPoints,
          characterTemplateIds,
          worldId
        )
    )
  )
}

export const mvmConfirmBattle = (walletAddress: string, attackId: string) => {
  const w = (walletAddress || '').toLowerCase().trim()
  return dedupeMvmOp(`confirm|${attackId}|${w}`, () =>
    emitWithAck('mvm:confirm-battle', { walletAddress, attackId }, () => restMvmConfirmBattle(walletAddress, attackId))
  )
}

export const mvmAutoDefend = (walletAddress: string, attackId: string) => {
  const w = (walletAddress || '').toLowerCase().trim()
  return dedupeMvmOp(`autodef|${attackId}|${w}`, () =>
    emitWithAck('mvm:auto-defend', { walletAddress, attackId }, () => restMvmAutoDefend(walletAddress, attackId))
  )
}

export const joinMvMRally = (
  walletAddress: string,
  attackId: string,
  selectedCharacterIds: string[],
  characterTemplateIds?: string[],
  abilityPoints?: unknown,
  breadAmount?: number
) => {
  const w = (walletAddress || '').toLowerCase().trim()
  const ck = [...selectedCharacterIds].map((id) => String(id)).sort().join(',')
  return dedupeMvmOp(`rally|${attackId}|${w}|${ck}`, () =>
    emitWithAck(
      'mvm:join-rally',
      {
        walletAddress,
        attackId,
        selectedCharacterIds,
        characterTemplateIds,
        abilityPoints,
        ...(typeof breadAmount === 'number' && Number.isFinite(breadAmount) ? { breadAmount } : {}),
      },
      () => restJoinMvMRally(walletAddress, attackId, selectedCharacterIds, characterTemplateIds, abilityPoints, breadAmount)
    )
  )
}

export const joinMvMReinforcement = (
  walletAddress: string,
  attackId: string,
  selectedCharacterIds: string[],
  characterTemplateIds?: string[],
  breadAmount?: number
) => {
  const w = (walletAddress || '').toLowerCase().trim()
  const ck = [...selectedCharacterIds].map((id) => String(id)).sort().join(',')
  return dedupeMvmOp(`reinf|${attackId}|${w}|${ck}`, () =>
    emitWithAck(
      'mvm:join-reinforcement',
      {
        walletAddress,
        attackId,
        selectedCharacterIds,
        characterTemplateIds,
        ...(typeof breadAmount === 'number' && Number.isFinite(breadAmount) ? { breadAmount } : {}),
      },
      () => restJoinMvMReinforcement(walletAddress, attackId, selectedCharacterIds, characterTemplateIds, breadAmount)
    )
  )
}

export const requestDefenseHelp = (walletAddress: string, attackId: string) => {
  const w = (walletAddress || '').toLowerCase().trim()
  return dedupeMvmOp(`help|${attackId}|${w}`, () =>
    emitWithAck(
      'mvm:request-defense-help',
      { walletAddress, attackId },
      () => restRequestDefenseHelp(walletAddress, attackId)
    )
  )
}

export const leaveMvMRallyJoin = (walletAddress: string, attackId: string) => {
  const w = (walletAddress || '').toLowerCase().trim()
  return dedupeMvmOp(`leave-rally|${attackId}|${w}`, () =>
    emitWithAck(
      'mvm:leave-rally-join',
      { walletAddress, attackId },
      () => restLeaveMvMRallyJoin(walletAddress, attackId)
    )
  )
}

export const leaveMvMReinforcement = (walletAddress: string, attackId: string) => {
  const w = (walletAddress || '').toLowerCase().trim()
  return dedupeMvmOp(`leave-reinf|${attackId}|${w}`, () =>
    emitWithAck(
      'mvm:leave-reinforcement',
      { walletAddress, attackId },
      () => restLeaveMvMReinforcement(walletAddress, attackId)
    )
  )
}
